import {
  Box,
  Button,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Check, Circle, CircleCheckBig, Plus, Undo2 } from 'lucide-react';
import { formatShortDate } from '@duke-hq/djs';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { StudentWorkPlacementJourneyStatus } from '@duke-hq/svc-api-db/client';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { StatusPill } from '../components/StatusPill';
import { SetUpContractModal } from './SetUpContractModal';
import { isContractOutOfDate } from './contractOutOfDate';
import { isChangeableStage } from './journeyStages';
import { partyLabel } from './contractParty';
import { testIds } from './testIds';

type Contract = NonNullable<
  RouterOutputs['workPlacements']['placements']['journeyByEnrolment']['contract']
>;
type Signature = Contract['signatures'][number];

function contractChangeBlockedReason({
  journeyStatus,
  journeyOnHold,
  enrolmentWithdrawn,
  studentPlaced,
}: {
  journeyStatus: StudentWorkPlacementJourneyStatus;
  journeyOnHold: boolean;
  enrolmentWithdrawn: boolean;
  studentPlaced: boolean;
}): string | null {
  if (enrolmentWithdrawn) {
    return t`The enrolment has been withdrawn.`;
  }
  if (journeyOnHold) {
    return t`This student's placement journey is on hold.`;
  }
  if (studentPlaced) {
    return t`The student has already been placed.`;
  }
  if (journeyStatus !== StudentWorkPlacementJourneyStatus.AWAITING_CONTRACT) {
    return t`Available once the stage is moved to Contracts.`;
  }
  return null;
}

function ContractStatus({ contract }: { contract: Contract }): React.ReactElement {
  if (contract.isComplete) {
    return (
      <StatusPill
        label={t`Complete`}
        colorScheme="green"
        data-testid={testIds.contractStatusBadge}
      />
    );
  }
  const outstanding = contract.signatures.find((signature) => signature.receivedAt == null);
  return (
    <StatusPill
      label={
        outstanding != null ? t`Awaiting ${partyLabel(outstanding.party)}` : t`Awaiting signatures`
      }
      colorScheme="orange"
      data-testid={testIds.contractStatusBadge}
    />
  );
}

function ContractField({
  label,
  children,
  'data-testid': dataTestId,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  'data-testid': string;
}): React.ReactElement {
  return (
    <Stack gap={0} data-testid={dataTestId}>
      <Text fontSize="xs" color="fg.muted">
        {label}
      </Text>
      <Text>{children}</Text>
    </Stack>
  );
}

function SignatureRow({
  signature,
  canChange,
  isSaving,
  onMark,
}: {
  signature: Signature;
  canChange: boolean;
  isSaving: boolean;
  onMark: (received: boolean) => void;
}): React.ReactElement {
  const receivedOn = signature.receivedAt != null ? formatShortDate(signature.receivedAt) : null;

  return (
    <HStack
      justify="space-between"
      gap={3}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      bg="bg.canvas"
      py={3}
      px={4}
      data-testid={testIds.contractSignatureRow}
    >
      <HStack gap={3}>
        <Box color={receivedOn != null ? 'success' : 'fg.subtle'} display="flex" flexShrink={0}>
          {receivedOn != null ? (
            <CircleCheckBig size={iconSize.lg} strokeWidth={2} />
          ) : (
            <Circle size={iconSize.lg} strokeWidth={2} />
          )}
        </Box>
        <Text fontWeight="medium">{partyLabel(signature.party)}</Text>
      </HStack>

      {receivedOn != null ? (
        <HStack gap={3} fontSize="sm" color="fg.muted" flexWrap="wrap" justify="end">
          <Text>
            {signature.markedByName != null
              ? t`Received ${receivedOn} · marked by ${signature.markedByName}`
              : t`Received ${receivedOn}`}
          </Text>
          <Button
            variant="secondary"
            size="xs"
            leftIcon={<Undo2 size={iconSize.sm} />}
            isDisabled={!canChange}
            isLoading={isSaving}
            onClick={() => onMark(false)}
            data-testid={testIds.undoSignatureButton}
          >
            <Trans>Undo</Trans>
          </Button>
        </HStack>
      ) : (
        <Button
          variant="secondary"
          size="xs"
          leftIcon={<Check size={iconSize.sm} />}
          isDisabled={!canChange}
          isLoading={isSaving}
          onClick={() => onMark(true)}
          data-testid={testIds.markSignatureButton}
        >
          <Trans>Mark received</Trans>
        </Button>
      )}
    </HStack>
  );
}

function ContractDetails({
  contract,
  canChange,
  onChanged,
}: {
  contract: Contract;
  canChange: boolean;
  onChanged: () => void;
}): React.ReactElement {
  const toast = useToast();

  const markSignature = trpcAdmin.workPlacements.placements.markContractSignature.useMutation({
    onSuccess: (result, variables) => {
      if (!result.changed) {
        toast({
          status: 'info',
          title: variables.received
            ? t`This signature was already marked as received`
            : t`This signature was already outstanding`,
        });
      }
      onChanged();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to update signature`, description: error.message });
      if (isContractOutOfDate(error)) {
        onChanged();
      }
    },
  });

  const savingParty = markSignature.isPending ? markSignature.variables?.party : undefined;
  const contactName =
    contract.employerContact != null
      ? `${contract.employerContact.firstName} ${contract.employerContact.lastName}`
      : '—';

  return (
    <Stack gap={4}>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
        <ContractField label={<Trans>Employer</Trans>} data-testid={testIds.contractEmployerField}>
          {contract.employer.name}
        </ContractField>
        <ContractField label={<Trans>Contact</Trans>} data-testid={testIds.contractContactField}>
          {contactName}
        </ContractField>
        <ContractField label={<Trans>Document</Trans>} data-testid={testIds.contractDocumentLink}>
          {contract.documentUrl != null ? (
            <Link
              href={contract.documentUrl}
              isExternal
              color="fg.accent.emphasis"
              fontWeight="medium"
            >
              <Trans>Placement agreement</Trans>
            </Link>
          ) : (
            '—'
          )}
        </ContractField>
      </SimpleGrid>

      <Stack gap={2}>
        {contract.signatures.map((signature) => (
          <SignatureRow
            key={signature.party}
            signature={signature}
            canChange={canChange && savingParty == null}
            isSaving={savingParty === signature.party}
            onMark={(received) =>
              markSignature.mutate({ contractId: contract.id, party: signature.party, received })
            }
          />
        ))}
      </Stack>
    </Stack>
  );
}

function RemoveContractButton({
  contract,
  isDisabled,
  onChanged,
}: {
  contract: Contract;
  isDisabled: boolean;
  onChanged: () => void;
}): React.ReactElement {
  const toast = useToast();
  const confirm = useDisclosure();
  const employerName = contract.employer.name;

  const deleteContract = trpcAdmin.workPlacements.placements.deletePlacementContract.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Contract removed` });
      confirm.onClose();
      onChanged();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to remove contract`, description: error.message });
      if (isContractOutOfDate(error)) {
        confirm.onClose();
        onChanged();
      }
    },
  });

  return (
    <>
      <Button
        variant="link"
        size="sm"
        color="danger"
        alignSelf="end"
        isDisabled={isDisabled}
        onClick={confirm.onOpen}
        data-testid={testIds.removeContractButton}
      >
        <Trans>Remove contract</Trans>
      </Button>
      <ConfirmationDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        title={<Trans>Remove contract?</Trans>}
        body={
          <Trans>
            All signatures on the contract with {employerName} will be removed too. You can create a
            new contract afterwards.
          </Trans>
        }
        confirmLabel={<Trans>Remove</Trans>}
        onConfirm={() => deleteContract.mutate({ contractId: contract.id })}
        isLoading={deleteContract.isPending}
        variant="destructive"
      />
    </>
  );
}

export function ContractSection({
  journeyId,
  journeyStatus,
  journeyOnHold,
  enrolmentWithdrawn,
  isRefreshing,
  courseId,
  contract,
  onChanged,
}: {
  journeyId: string;
  journeyStatus: StudentWorkPlacementJourneyStatus;
  journeyOnHold: boolean;
  enrolmentWithdrawn: boolean;
  isRefreshing: boolean;
  courseId: string | null;
  contract: Contract | null;
  onChanged: () => void;
}): React.ReactElement {
  const setUpModal = useDisclosure();
  const studentPlaced = !isChangeableStage(journeyStatus);
  const blockedReason = contractChangeBlockedReason({
    journeyStatus,
    journeyOnHold,
    enrolmentWithdrawn,
    studentPlaced,
  });
  const canChange = blockedReason == null && !isRefreshing;
  const receivedCount =
    contract?.signatures.filter((signature) => signature.receivedAt != null).length ?? 0;
  const signatureCount = contract?.signatures.length ?? 0;

  return (
    <Stack
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      p={5}
      gap={4}
      data-testid={testIds.contractSection}
    >
      <HStack justify="space-between" align="center" gap={3} flexWrap="wrap">
        <Stack gap={1}>
          <HStack gap={2} align="baseline">
            <Text fontSize="lg" fontWeight="semibold">
              <Trans>Contract</Trans>
            </Text>
            {contract != null ? (
              <Text fontSize="sm" color="fg.muted">
                <Trans>
                  {receivedCount}/{signatureCount} signatures
                </Trans>
              </Text>
            ) : null}
          </HStack>
          {contract == null ? (
            <Text fontSize="sm" color="fg.muted">
              {blockedReason == null ? (
                <Trans>No contract yet.</Trans>
              ) : studentPlaced ? (
                <Trans>No contract. {blockedReason}</Trans>
              ) : (
                <Trans>No contract yet. {blockedReason}</Trans>
              )}
            </Text>
          ) : null}
        </Stack>

        {contract != null ? (
          <ContractStatus contract={contract} />
        ) : (
          <Tooltip label={blockedReason} isDisabled={blockedReason == null} shouldWrapChildren>
            <Button
              leftIcon={<Plus size={iconSize.md} />}
              isDisabled={!canChange}
              onClick={setUpModal.onOpen}
              data-testid={testIds.setUpContractButton}
            >
              <Trans>Create contract</Trans>
            </Button>
          </Tooltip>
        )}
      </HStack>

      {contract != null ? (
        <>
          {contract.usedForPlacement ? (
            <Text fontSize="sm" color="fg.muted" data-testid={testIds.contractLockedReason}>
              <Trans>This contract was used to place the student.</Trans>
            </Text>
          ) : blockedReason != null ? (
            <Text fontSize="sm" color="fg.muted" data-testid={testIds.contractLockedReason}>
              <Trans>Signatures can't be marked right now. {blockedReason}</Trans>
            </Text>
          ) : null}
          <ContractDetails
            contract={contract}
            canChange={canChange && !contract.usedForPlacement}
            onChanged={onChanged}
          />
          {contract.usedForPlacement ? null : (
            <RemoveContractButton
              contract={contract}
              isDisabled={isRefreshing}
              onChanged={onChanged}
            />
          )}
        </>
      ) : null}

      {contract == null ? (
        <SetUpContractModal
          isOpen={setUpModal.isOpen}
          onClose={setUpModal.onClose}
          journeyId={journeyId}
          courseId={courseId}
          onSuccess={onChanged}
        />
      ) : null}
    </Stack>
  );
}
